
import React, { useEffect, useRef, useState, useContext } from 'react';
import { Grid, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useFormContext } from '../Context/Context';
import { useInputValues } from '../Context/InputValuesContext';
import { useErrorContext } from '../Context/ErrorContext';
import { useReadOnly } from '../Context/ReadOnlyContext';
import { FinalValuesContext } from '../Context/FinalValuesContext';
import { fillInputValue, handleCondition } from '../helpers/Address/helpers';
import AddressHeading from './Address/AddressHeading';
import AddressInputGroup from './Address/AddressInputGroup';

const Address = ({ id, header }) => {
  const location = useLocation();
  const currentPage = location.pathname;
  const theme = useTheme();
  const HeadingColor = theme.palette.blue[700];

  const { drawerData } = useFormContext();
  const { inputValues } = useInputValues();
  const { setMistake } = useErrorContext();
  const { readOnly } = useReadOnly();
  const { finalSelectValues } = useContext(FinalValuesContext);

  const existingComponentIndex = drawerData.findIndex(component => component.id === id);
  const isReadOnly = readOnly || currentPage.startsWith('/publish') || currentPage.startsWith('/card-form/publish') || currentPage.startsWith('/user/fill-form/');
  const [condition, setCondition] = useState(false);
  const gridRef = useRef(null);

  const [addressValue, setAddressValue] = useState(header || drawerData[existingComponentIndex]?.state.addressValue || "آدرس");

  const [streetInput, setStreetInput] = useState(drawerData[existingComponentIndex]?.state.mainStreetInput || '');
  const [streetValue, setStreetValue] = useState(drawerData[existingComponentIndex]?.state.streetValue || "آدرس خیابان");
  const [errorStreet, setErrorStreet] = useState('');

  const [streetInputSecond, setStreetInputSecond] = useState(drawerData[existingComponentIndex]?.state.mainStreetInputSecond || '');
  const [streetValueSecond, setStreetValueSecond] = useState(drawerData[existingComponentIndex]?.state.streetValueSecond || "آدرس خیابان خط دوم");
  const [errorStreetSecond, setErrorStreetSecond] = useState('');

  const [cityInput, setCityInput] = useState(drawerData[existingComponentIndex]?.state.mainCityInput || '');
  const [city, setCity] = useState(drawerData[existingComponentIndex]?.state.city || "شهر");
  const [errorCity, setErrorCity] = useState('');

  const [provinceInput, setProvinceInput] = useState(drawerData[existingComponentIndex]?.state.mainProvinceInput || '');
  const [province, setProvince] = useState(drawerData[existingComponentIndex]?.state.province || "ایالت / استان");
  const [errorProvince, setErrorProvince] = useState('');

  const [postalInput, setPostalInput] = useState(drawerData[existingComponentIndex]?.state.mainPostalInput || '');
  const [postal, setPostal] = useState(drawerData[existingComponentIndex]?.state.postal || "پست / کد پستی");
  const [errorPostal, setErrorPostal] = useState('');

  const handleHeadingFocus = (e) => e.target.select();
  const handleOutsideClick = (e) => {
    if (gridRef.current && !gridRef.current.contains(e.target)) setCondition(false);
  };

  const validateField = (value, patternName, setError) => {
    const pattern = inputValues.find(item => item.name === patternName);
    if (pattern && !new RegExp(pattern.regex || '^[a-zA-Zآ-ی\\s]+$').test(value)) {
      setError(pattern.value);
      setMistake(true);
    } else {
      setError('');
      setMistake(false);
    }
  };

  const handleStreetInputChange = (e) => {
    const value = e.target.value;
    setStreetInput(value);
    validateField(value, "ThisFieldCanOnlyContainLetters", setErrorStreet);
  };

  const handleStreetInputSecondChange = (e) => {
    const value = e.target.value;
    setStreetInputSecond(value);
    validateField(value, "ThisFieldCanOnlyContainLetters", setErrorStreetSecond);
  };

  const handleCityInputChange = (e) => {
    const value = e.target.value;
    setCityInput(value);
    validateField(value, "ThisFieldCanOnlyContainLetters", setErrorCity);
  };

  const handleProvinceInputChange = (e) => {
    const value = e.target.value;
    setProvinceInput(value);
    validateField(value, "ThisFieldCanOnlyContainLetters", setErrorProvince);
  };

  const handlePostalInputChange = (e) => {
    const value = e.target.value;
    setPostalInput(value);
    if (isNaN(value)) {
      const errorItem = inputValues.find(item => item.name === "ThisFieldCanOnlyContainNumericValues");
      if (errorItem) {
        setErrorPostal(errorItem.value);
        setMistake(true);
      }
    } else {
      setErrorPostal('');
      setMistake(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  useEffect(() => {
    fillInputValue();
  }, [id, addressValue, streetInput, streetValue, streetInputSecond, streetValueSecond, cityInput, city, provinceInput, province, postalInput, postal]);

  useEffect(() => {
    const dataIndex = drawerData.findIndex(item => item.title === addressValue);
    const dataCondition = drawerData[dataIndex]?.state.readOnly;
    handleCondition(
      currentPage,
      readOnly,
      dataCondition,
      addressValue,
      setCondition
    );
  }, [finalSelectValues, readOnly, currentPage, drawerData, addressValue, setCondition]);
  

  return (
    <Grid
      className={'Address'}
      mb={currentPage.startsWith('/build') || currentPage.startsWith('/publish') ? '28px' : '50px'}
      p={drawerData.length > 0 ? (currentPage.startsWith('/build') || currentPage.startsWith('/publish') || currentPage.startsWith('/user/fill-form') ? '12px 10px' : { xs: '12px', md: '24px' }) : '0px'}
      mx={currentPage === '/build' || currentPage === '/publish' ? '4px' : '0px'}
      borderRadius={'3px'}
      width={'100%'}
      bgcolor={'white'}
      ref={gridRef}
      onClick={() => setCondition(true)}
      border={condition && drawerData.length > 0 && !isReadOnly ? '2px solid #0293e2' : '2px solid transparent'}
    >
      <Grid pb={'24px'}>
        <AddressHeading
          addressValue={addressValue}
          handleAddressChange={(e) => setAddressValue(e.target.value)}
          handleHeadingFocus={handleHeadingFocus}
          isReadOnly={isReadOnly}
          condition={condition}
        />
      </Grid>

      <AddressInputGroup
        mainValue={streetInput}
        onMainChange={handleStreetInputChange}
        mainError={errorStreet}
        subValue={streetValue}
        onSubChange={(e) => setStreetValue(e.target.value)}
        readOnly={condition}
        isReadOnly={isReadOnly}
        condition={condition}
        headingColor={HeadingColor}
        handleHeadingFocus={handleHeadingFocus}
      />

      <AddressInputGroup
        mainValue={streetInputSecond}
        onMainChange={handleStreetInputSecondChange}
        mainError={errorStreetSecond}
        subValue={streetValueSecond}
        onSubChange={(e) => setStreetValueSecond(e.target.value)}
        readOnly={condition}
        isReadOnly={isReadOnly}
        condition={condition}
        headingColor={HeadingColor}
        handleHeadingFocus={handleHeadingFocus}
      />

      <Grid display={'flex'} alignItems={'center'} justifyContent={'space-between'} gap={{ xs: '8px', xxs: '24px' }} pb={'24px'}>
        <Grid width={'50%'}>
          <AddressInputGroup
            mainValue={cityInput}
            onMainChange={handleCityInputChange}
            mainError={errorCity}
            subValue={city}
            onSubChange={(e) => setCity(e.target.value)}
            readOnly={condition}
            isReadOnly={isReadOnly}
            condition={condition}
            headingColor={HeadingColor}
            handleHeadingFocus={handleHeadingFocus}
          />
        </Grid>
        <Grid width={'50%'}>
          <AddressInputGroup
            mainValue={provinceInput}
            onMainChange={handleProvinceInputChange}
            mainError={errorProvince}
            subValue={province}
            onSubChange={(e) => setProvince(e.target.value)}
            readOnly={condition}
            isReadOnly={isReadOnly}
            condition={condition}
            headingColor={HeadingColor}
            handleHeadingFocus={handleHeadingFocus}
          />
        </Grid>
      </Grid>

      <AddressInputGroup
        mainValue={postalInput}
        onMainChange={handlePostalInputChange}
        mainError={errorPostal}
        subValue={postal}
        onSubChange={(e) => setPostal(e.target.value)}
        readOnly={condition}
        isReadOnly={isReadOnly}
        condition={condition}
        headingColor={HeadingColor}
        handleHeadingFocus={handleHeadingFocus}
      />
    </Grid>
  );
};

export default Address;